import { useMemo, useState } from "react";
import type { NewsletterComposeDraft, NewsletterDefaultDesignSnapshot } from "@easy-types";
import { IconLabel } from "./eventPlannerIcons";
import {
  buildNewsletterComposeDraft,
  copyHtmlToClipboard,
  filterNewsletterEvents,
  formatNewsletterPeriodLabel,
  pluralize,
  statusClassName,
  type EventRecord,
  type StatusState,
} from "./eventPlannerUtils";
import "./newsletterFromEventsBuilder.css";

type NewsletterPeriod = {
  startDate: string;
  endDate: string;
};

type NewsletterFromEventsBuilderProps = {
  events: EventRecord[];
  period: NewsletterPeriod;
  defaultDesign?: NewsletterDefaultDesignSnapshot | null;
  canOpenNewsletterComposer: boolean;
  onPeriodChange: (period: NewsletterPeriod) => void;
  onOpenNewsletterDraft: (draft: NewsletterComposeDraft) => void;
};

export function NewsletterFromEventsBuilder({
  events,
  period,
  defaultDesign,
  canOpenNewsletterComposer,
  onPeriodChange,
  onOpenNewsletterDraft,
}: NewsletterFromEventsBuilderProps) {
  const [status, setStatus] = useState<StatusState>({ message: "", tone: "neutral" });
  const newsletterEvents = useMemo(
    () => filterNewsletterEvents(events, period.startDate, period.endDate),
    [events, period.endDate, period.startDate]
  );
  const newsletterDraft = useMemo(
    () => buildNewsletterComposeDraft(events, period.startDate, period.endDate, defaultDesign),
    [defaultDesign, events, period.endDate, period.startDate]
  );

  async function handleCopyNewsletter() {
    try {
      await copyHtmlToClipboard(newsletterDraft.html_body);
      setStatus({
        message: "Newsletter copiee avec sa mise en forme. Vous pouvez la coller dans votre expediteur.",
        tone: "success",
      });
    } catch (error) {
      setStatus({
        message: error instanceof Error ? error.message : "Impossible de copier la newsletter.",
        tone: "error",
      });
    }
  }

  function handleOpenNewsletter() {
    if (!canOpenNewsletterComposer) {
      setStatus({
        message: "La newsletter est disponible depuis l'espace Relyo.",
        tone: "error",
      });
      return;
    }

    try {
      onOpenNewsletterDraft(newsletterDraft);
      setStatus({
        message: "Newsletter envoyée vers la page Newsletter. Complétez les destinataires puis planifiez l'envoi.",
        tone: "success",
      });
    } catch (error) {
      setStatus({
        message: error instanceof Error ? error.message : "Impossible d'ouvrir la newsletter.",
        tone: "error",
      });
    }
  }

  return (
    <div className="newsletter-builder-grid">
      <section className="newsletter-builder-sidebar">
        <div className="newsletter-period-grid">
          <label>
            Date de début
            <input
              type="date"
              value={period.startDate}
              onChange={(event) => {
                setStatus({ message: "", tone: "neutral" });
                onPeriodChange({ ...period, startDate: event.target.value });
              }}
            />
          </label>
          <label>
            Date de fin
            <input
              type="date"
              value={period.endDate}
              onChange={(event) => {
                setStatus({ message: "", tone: "neutral" });
                onPeriodChange({ ...period, endDate: event.target.value });
              }}
            />
          </label>
        </div>

        <div className="detail-meta-list">
          <div className="detail-meta-item">
            <strong>Periode</strong>
            <span>{formatNewsletterPeriodLabel(period.startDate, period.endDate)}</span>
          </div>
          <div className="detail-meta-item">
            <strong>Évènements publiés</strong>
            <span>{newsletterEvents.length}</span>
          </div>
          <div className="detail-meta-item">
            <strong>Objet pre-rempli</strong>
            <span>{newsletterDraft.subject}</span>
          </div>
        </div>

        <p className="event-form-help">
          Les brouillons sont exclus automatiquement. Le contenu génère un HTML enrichi compatible avec la newsletter et
          avec le collage dans d'autres editeurs.
        </p>

        <div className="event-summary-actions">
          <button
            type="button"
            className="button"
            disabled={newsletterEvents.length === 0}
            onClick={() => void handleCopyNewsletter()}
          >
            <IconLabel icon="copy" label="Copier la newsletter" />
          </button>
          <button
            type="button"
            className="button button-primary"
            disabled={!canOpenNewsletterComposer || newsletterEvents.length === 0}
            onClick={handleOpenNewsletter}
          >
            <IconLabel icon="mail" label="Ouvrir dans Newsletter" />
          </button>
        </div>

        {!canOpenNewsletterComposer ? (
          <p className="event-form-help">
            L'ouverture directe dans Newsletter est disponible quand Évènements tourne dans l'espace Relyo.
          </p>
        ) : null}

        <p className={statusClassName(status)}>{status.message}</p>
      </section>

      <section className="newsletter-preview-shell">
        <div className="newsletter-preview-head">
          <div>
            <p className="section-kicker">Aperçu</p>
            <h3>Newsletter générée</h3>
          </div>
          <span className="event-count-badge">
            {newsletterEvents.length} {pluralize(newsletterEvents.length, "évènement")}
          </span>
        </div>

        <div
          className={`newsletter-preview${newsletterEvents.length === 0 ? " is-empty" : ""}`}
          dangerouslySetInnerHTML={{ __html: newsletterDraft.html_body }}
        />
      </section>
    </div>
  );
}
